import apiClient from "../api"

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const unwrap = (error, fallback) =>
  error?.response?.data || error || { message: fallback };

/**
 * SPEAKON API client.
 *
 * Every call is scoped by the authenticated GESTOCK JWT. The backend derives the
 * tenant itself, so no storeId is trusted from here beyond an explicit user choice.
 */
export const aiApi = {
  // --- status -------------------------------------------------------------
  health: async () => {
    try {
      const response = await apiClient.get("gestock/ai/health");
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de vérifier le statut de l'assistant IA.");
    }
  },

  // --- conversation -------------------------------------------------------
  identity: async (storeId) => {
    try {
      const response = await apiClient.get("gestock/ai/identity", {
        headers: authHeaders(), params: { storeId },
      });
      return response.data.identity;
    } catch (error) {
      throw unwrap(error, "Connexion privée indisponible.");
    }
  },

  chat: async ({ message, images = [], conversationId, storeId, language = "fr" }) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/chat",
        { message, images, conversationId, storeId, language },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "L'assistant est indisponible.");
    }
  },

  conversations: async () => {
    try {
      const response = await apiClient.get("gestock/ai/conversations", {
        headers: authHeaders(),
      });
      return response.data.conversations || [];
    } catch (error) {
      throw unwrap(error, "Impossible de charger les conversations.");
    }
  },

  conversationMessages: async (conversationId) => {
    try {
      const response = await apiClient.get(
        `gestock/ai/conversations/${conversationId}/messages`,
        { headers: authHeaders() }
      );
      return response.data.messages || [];
    } catch (error) {
      throw unwrap(error, "Impossible de charger la conversation.");
    }
  },

  // --- image to product ---------------------------------------------------
  analyzeProduct: async ({ images, hint, storeId }) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/products/draft",
        { images, hint, storeId },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Analyse du produit impossible.");
    }
  },

  buildDraft: async ({ images, hint, quantity, sellPrice, buyPrice, storeHint, conversationId }) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/products/draft",
        { images, hint, quantity, sellPrice, buyPrice, storeHint, conversationId },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Préparation de la fiche impossible.");
    }
  },

  confirmDraft: async ({ draft, approved = true, conversationId }) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/products/confirm",
        { draft, approved, conversationId },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Enregistrement du produit impossible.");
    }
  },

  // --- inventory ----------------------------------------------------------
  inventory: async ({ search = "", lowStock = false } = {}) => {
    try {
      const response = await apiClient.get("gestock/ai/inventory", {
        headers: authHeaders(),
        params: { search, lowStock: lowStock ? "1" : undefined },
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger le stock.");
    }
  },

  /** Generate AI image variants for a product draft (background task). */
  requestImageVariants: async ({ images, styleIds, model, quality }) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/products/variants",
        { images, styleIds, model, quality },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Génération des rendus impossible.");
    }
  },

  /** Re-mint fresh presigned URLs for a finished variants task. */
  previewImageVariants: async (taskId) => {
    try {
      const response = await apiClient.get(
        `gestock/ai/products/variants/preview`,
        { headers: authHeaders(), params: { taskId } }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de rafraîchir les visuels.");
    }
  },

  /** Create (or reuse) a structured product variant: size / pointure / colour. */
  createProductVariant: async (payload) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/products/variant",
        payload,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Création de la variante impossible.");
    }
  },

  /** Add stock to an existing product (used when a duplicate is detected). */
  adjustStock: async ({ productId, delta, buyPrice, reason }) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/inventory/adjust",
        { productId, delta, buyPrice, reason },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Ajustement de stock impossible.");
    }
  },

  // --- leads --------------------------------------------------------------
  discoverLeads: async (productId) => {
    try {
      const response = await apiClient.post(
        "gestock/ai/leads/discover",
        { productId },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Recherche de clients impossible.");
    }
  },

  leads: async ({ page = 1, limit = 25, status } = {}) => {
    try {
      const response = await apiClient.get("gestock/ai/leads", {
        headers: authHeaders(),
        params: { page, limit, status: status || undefined },
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger les opportunités.");
    }
  },

  updateLead: async (leadId, patch) => {
    try {
      const response = await apiClient.patch(
        `gestock/ai/leads/${leadId}`,
        patch,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Mise à jour impossible.");
    }
  },

  // --- tasks --------------------------------------------------------------
  tasks: async ({ page = 1, limit = 20, status } = {}) => {
    try {
      const response = await apiClient.get("gestock/ai/tasks", {
        headers: authHeaders(),
        params: { page, limit, status: status || undefined },
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger les tâches.");
    }
  },

  task: async (taskId) => {
    try {
      const response = await apiClient.get(`gestock/ai/tasks/${taskId}`, {
        headers: authHeaders(),
      });
      return response.data.task;
    } catch (error) {
      throw unwrap(error, "Impossible de charger la tâche.");
    }
  },

  cancelTask: async (taskId) => {
    try {
      const response = await apiClient.post(
        `gestock/ai/tasks/${taskId}/cancel`,
        {},
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Annulation impossible.");
    }
  },

  // --- settings / usage / activity ---------------------------------------
  settings: async () => {
    try {
      const response = await apiClient.get("gestock/ai/settings", {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger les réglages.");
    }
  },

  updateSettings: async (patch) => {
    try {
      const response = await apiClient.put("gestock/ai/settings", patch, {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Enregistrement des réglages impossible.");
    }
  },

  usage: async () => {
    try {
      const response = await apiClient.get("gestock/ai/usage", {
        headers: authHeaders(),
      });
      return response.data;
    } catch (error) {
      throw unwrap(error, "Impossible de charger l'utilisation.");
    }
  },

  activity: async (limit = 50) => {
    try {
      const response = await apiClient.get("gestock/ai/activity", {
        headers: authHeaders(),
        params: { limit },
      });
      return response.data.entries || [];
    } catch (error) {
      throw unwrap(error, "Impossible de charger l'activité.");
    }
  },

  // --- campaigns ----------------------------------------------------------
  campaigns: async () => {
    try {
      const response = await apiClient.get("gestock/ai/campaigns", {
        headers: authHeaders(),
      });
      return response.data.campaigns || [];
    } catch (error) {
      throw unwrap(error, "Impossible de charger les campagnes.");
    }
  },

  approveCampaign: async (campaignId, budgetFcfa) => {
    try {
      const response = await apiClient.post(
        `gestock/ai/campaigns/${campaignId}/approve`,
        { budgetFcfa },
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      throw unwrap(error, "Approbation impossible.");
    }
  },
};

export default aiApi;